import memoize from "lodash/memoize";
import FetchError from "logic/errors/FetchError";
import ErrorsActions from "actions/errors/ErrorsActions";
import { createFromFetchError } from "logic/errors/ReportedErrors";
import CancellablePromise from "logic/rest/CancellablePromise";
import { ServerAvailabilityActions } from "stores/sessions/ServerAvailabilityStore";
const importSessionStore = memoize(() => require("stores/sessions/SessionStore"));
const reportServerSuccess = () => {
  ServerAvailabilityActions.reportSuccess();
};
const defaultOnUnauthorizedError = (error) => ErrorsActions.report(createFromFetchError(error));
const emptyToUndefined = (s) => s && s !== "" ? s : void 0;
const onServerError = async (error, onUnauthorized = defaultOnUnauthorizedError) => {
  var _a, _b;
  const contentType = (_a = error.headers) == null ? void 0 : _a.get("Content-Type");
  const response = await ((contentType == null ? void 0 : contentType.startsWith("application/json")) ? error.json().then((body) => body) : (_b = error == null ? void 0 : error.text) == null ? void 0 : _b.call(error));
  const { SessionStore, SessionActions } = importSessionStore();
  const fetchError = new FetchError(error.statusText, error.status, emptyToUndefined(response));
  if (SessionStore.isLoggedIn() && error.status === 401) {
    SessionActions.logout();
  }
  if (SessionStore.isLoggedIn() && error.status === 403) {
    onUnauthorized(fetchError);
  }
  if (error && !error.status) {
    ServerAvailabilityActions.reportError(fetchError);
  }
  throw fetchError;
};
const maybeStringify = (body) => body && typeof body !== "string" ? JSON.stringify(body) : body;
const defaultResponseHandler = (resp) => {
  if (resp.ok) {
    const { status } = resp;
    const contentLength = Number.parseInt(resp.headers.get("Content-Length"), 10);
    const noContent = status === 204 || contentLength === 0;
    reportServerSuccess();
    return noContent ? null : resp.json();
  }
  throw resp;
};
const textResponseHandler = (resp) => {
  if (resp.ok) {
    reportServerSuccess();
    return resp.text();
  }
  throw resp;
};
export class Builder {
  constructor(method, url) {
    this.options = {};
    this.method = method;
    this.url = url.replace(/([^:])\/\//, "$1/");
    this.options = {
      "X-Requested-With": "XMLHttpRequest",
      "X-Requested-By": "XMLHttpRequest"
    };
    this.responseHandler = (response) => response;
    this.errorHandler = void 0;
  }
  setHeader(header, value) {
    this.options = {
      ...this.options,
      [header]: value
    };
    return this;
  }
  json(body) {
    this.body = { body: maybeStringify(body), mimeType: "application/json" };
    this.accept = "application/json";
    this.responseHandler = defaultResponseHandler;
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  formData(body, acceptedMimeType = "application/json") {
    this.body = { body };
    this.accept = acceptedMimeType;
    this.responseHandler = defaultResponseHandler;
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  file(body, mimeType) {
    this.body = { body: maybeStringify(body), mimeType: "application/json" };
    this.accept = mimeType;
    this.responseHandler = (resp) => {
      if (resp.ok) {
        reportServerSuccess();
        return resp.text();
      }
      throw resp;
    };
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  blobFile(body, mimeType) {
    this.body = { body: maybeStringify(body), mimeType: "application/json" };
    this.accept = mimeType;
    this.responseHandler = (resp) => {
      if (resp.ok) {
        reportServerSuccess();
        return resp.blob();
      }
      throw resp;
    };
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  plaintext(body) {
    this.body = { body, mimeType: "text/plain" };
    this.accept = "application/json";
    this.responseHandler = defaultResponseHandler;
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  streamingplaintext(body) {
    this.body = { body, mimeType: "text/plain" };
    this.accept = "text/plain";
    this.responseHandler = textResponseHandler;
    this.errorHandler = (error) => onServerError(error);
    return this;
  }
  ignoreUnauthorized() {
    this.errorHandler = (error) => onServerError(error, () => {
    });
    return this;
  }
  noSessionExtension() {
    this.options = {
      ...this.options,
      "X-Graylog-No-Session-Extension": "true"
    };
    return this;
  }
  build() {
    const headers = this.body && this.body.mimeType ? { ...this.options, "Content-Type": this.body.mimeType } : this.options;
    if (this.accept) {
      headers.Accept = this.accept;
    }
    return CancellablePromise.of(
      window.fetch(this.url, {
        method: this.method,
        headers,
        body: this.body ? this.body.body : void 0
      })
    ).then(this.responseHandler, this.errorHandler).catch(this.errorHandler);
  }
}
function queuePromiseIfNotLoggedin(promise) {
  const { SessionStore, SessionActions } = importSessionStore();
  if (!SessionStore.isLoggedIn()) {
    return () => CancellablePromise.of(
      new Promise((resolve, reject) => {
        SessionActions.login.completed.listen(() => {
          promise().then(resolve, reject);
        });
      })
    );
  }
  return promise;
}
export default function fetch(method, url, body, requireSession = true) {
  const promise = () => new Builder(method, url).json(body).build();
  if (requireSession) {
    return queuePromiseIfNotLoggedin(promise)();
  }
  return promise();
}
export function fetchMultiPartFormData(url, body, requireSession = true) {
  const promise = () => new Builder("POST", url).formData(body).build();
  if (requireSession) {
    return queuePromiseIfNotLoggedin(promise)();
  }
  return promise();
}
export function fetchPlainText(method, url, body) {
  const promise = () => new Builder(method, url).plaintext(body).build();
  return queuePromiseIfNotLoggedin(promise)();
}
export function fetchStreamingPlainText(method, url, body) {
  const promise = () => new Builder(method, url).streamingplaintext(body).build();
  return queuePromiseIfNotLoggedin(promise)();
}
export function fetchPeriodically(method, url, body) {
  const promise = () => new Builder(method, url).noSessionExtension().json(body).build();
  return queuePromiseIfNotLoggedin(promise)();
}
export function fetchFile(method, url, body, mimeType = "text/csv") {
  const promise = () => new Builder(method, url).file(body, mimeType).build();
  return queuePromiseIfNotLoggedin(promise)();
}
export function fetchBlobFile(method, url, body, mimeType = "text/csv") {
  const promise = () => new Builder(method, url).blobFile(body, mimeType).build();
  return queuePromiseIfNotLoggedin(promise)();
}
